<script lang="ts">
	import { goto } from '$app/navigation';
	import { marketsConfig } from '$lib/config/markets';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import {
		currentClosePosition,
		popupModalOpen,
		popupModalType,
		popupModalData,
		closePositionOrderType
	} from '$lib/popups/popupsStore';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import type { Position } from '$lib/types/position';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { marketPrices } from '$lib/util/marketHelper';
	import ProcessingText from '$lib/util/ProcessingText.svelte';
	import { getNumBackInUnits, addDollarSign, makeBodyUnscrollable, num } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import { positionsBeingUpdated, tpslBeingUpdated } from '../../store';
	import { getDisplayAsset } from '../../tradingView/util';
	import { closingPosition, longOrShort, userMarginList } from '../../userInteract/store';
	import { openAdjustMarginModal } from '../../userInteract/userInteract';
	import UserPositionTpslItem from './UserPositionTPSLItem.svelte';

	export let position: Position;
	let isUnrealisedPnlPositive: boolean = true;

	$: positionPnl = new BigNumber(
		new BigNumber($marketPrices[position.marketIdentifier]).minus(position.avgEntryPrice)
	)
		.multipliedBy(position.size)
		.div(1e18)
		.plus(position.accruedFunding);

	$: if (positionPnl) {
		isUnrealisedPnlPositive = new BigNumber(positionPnl).gte(0);
	}

	$: isAccruedFudningPositive = new BigNumber(getNumBackInUnits(position.accruedFunding, 18, 3)).gt(
		0
	);
	$: isAccruedFudningNegative = new BigNumber(getNumBackInUnits(position.accruedFunding, 18, 3)).lt(
		0
	);
	export let showClosePosition = true;
	function openMarket() {
		if (position.marketIdentifier) {
			goto(`/perps?market=${position.marketIdentifier}`);
			longOrShort.set(new BigNumber(position.size).isGreaterThan(0) ? 'Long' : 'Short');
		}
	}

	function showShareablePnlPopup() {
		popupModalData.set(position.marketIdentifier);
		popupModalType.set('SharePnL');
		popupModalOpen.set(true);
	}

	function openClosePositionModal(type: 'Market' | 'Limit') {
		$currentClosePosition = position;
		$popupModalType = 'ClosePosition';
		$closePositionOrderType = type;
		$popupModalOpen = true;
		makeBodyUnscrollable();
	}

	let amount: string = '';
	$: if (position) {
		amount = new BigNumber(position.size)
			.multipliedBy($marketPrices[position.marketIdentifier])
			.div(1e18)
			.multipliedBy(-1)
			.toString();
	}

	$: positionMargin = $userMarginList.find(
		(margin) => margin.marketIdentifier === position.marketIdentifier
	)?.depositedMargin;

	let isUpdating: boolean = true;
	$: isUpdating = $positionsBeingUpdated.includes(position.asset);
	$: isCreating = position.margin == '';

	let isTpslUpdating: boolean = true;
	$: isTpslUpdating = $tpslBeingUpdated.includes(position.asset);
</script>

<button
	class="hidden sm:grid border-t border-t-fx-grey-600 py-4 items-center hover:bg-fx-grey-900 active:bg-fx-grey-700 cursor-pointer px-2 rounded-lg squircle-lg w-full
	{showClosePosition ? 'sm:grid-cols-10' : 'grid-cols-9'}"
	on:click={openMarket}
	on:keydown={openMarket}
>
	<div class="flex gap-2 items-center">
		<img loading="lazy" src={getTokenLogo(position.marketIdentifier)} alt="logo" class="w-5 h-5 rounded-full" />
		<div class="text-fx-grey-100 text-sm rounded-lg squircle-lg font-medium">
			{getDisplayName(position.marketIdentifier)}
		</div>
	</div>
	<GradientBorder
		gradient="bg-fx-gradient-6"
		paddingClass="p-px"
		borderRadius="rounded-lg squircle-lg"
		outerClassList="w-fit my-auto mx-auto h-fit"
		innerClassList="/''bg-fx-black"
	>
		{#if new BigNumber(position.size).gt(0)}
			<div
				class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg squircle-lg"
			>
				Long
			</div>
		{:else}
			<div
				class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg squircle-lg"
			>
				Short
			</div>
		{/if}
	</GradientBorder>
	<div class="flex">
		<div class="flex flex-col gap-1 items-center justify-center mx-auto">
			<div class="text-fx-grey-100 text-xs font-medium text-center">
				{getNumBackInUnits(position.size, 18, 4)}
				{getDisplayAsset(position.asset)}
			</div>
			<div class="text-fx-grey-300 text-xs font-medium text-center">
				{#if position}
					${num(
						getNumBackInUnits(
							new BigNumber(position.size)
								.multipliedBy($marketPrices[position.marketIdentifier])
								.div(1e18)
								.abs()
								.toString(),
							18,
							2
						)
					)}
				{:else}
					-
				{/if}
			</div>
		</div>
	</div>
	<div class="text-fx-grey-100 text-xs font-medium text-center">
		{isCreating
			? '-'
			: '$' +
			  num(
					getNumBackInUnits(position.avgEntryPrice, 18, marketsConfig[position.asset].showDecimals)
			  )}
	</div>
	<div class="flex justify-center">
		<div class="text-fx-grey-100 text-xs font-medium text-center">
			{isCreating
				? '-'
				: '$' +
				  num(
						getNumBackInUnits(
							position.liquidationPrice,
							18,
							marketsConfig[position.asset].showDecimals
						)
				  )}
		</div>
	</div>

	<div class="flex gap-2 justify-center items-center">
		{#if isCreating}
			-
		{:else}
			<div class="flex flex-col gap-1 items-start justify-center">
				<div class="text-xs font-medium text-center">
					{#if isUnrealisedPnlPositive}
						<span class="text-fx-gradient-23">
							{addDollarSign(getNumBackInUnits(positionPnl.toString(), 18, 2))}
						</span>
					{:else}
						<span class="text-fx-gradient-22">
							{addDollarSign(getNumBackInUnits(positionPnl.toString(), 18, 2))}
						</span>
					{/if}
				</div>
				{#if position}
					<div class="text-fx-grey-300 text-xs font-medium text-center">
						{new BigNumber(positionPnl)
							.div(new BigNumber(position.margin))
							.multipliedBy(100)
							.toFixed(2)}%
					</div>
				{/if}
			</div>
			<Tooltip title="Get a sharable image" header="Share">
				<button class="h-full" on:click|stopPropagation={showShareablePnlPopup}>
					<img class="w-4" loading="lazy" src={'share-icon.svg'} alt="share" />
				</button>
			</Tooltip>
		{/if}
	</div>

	<div class="flex justify-center">
		<div
			class:text-fx-gradient-23={isAccruedFudningPositive}
			class:text-fx-gradient-22={isAccruedFudningNegative}
			class="text-fx-grey-100 text-xs font-medium text-center"
		>
			{isCreating ? '-' : addDollarSign(getNumBackInUnits(position.accruedFunding, 18, 3))}
		</div>
	</div>

	<button
		class="flex gap-1 items-start justify-center mx-auto underline underline-offset-2 cursor-pointer"
		on:click|stopPropagation={() => openAdjustMarginModal(position.marketIdentifier)}
	>
		<div class="text-fx-grey-100 text-xs font-medium text-center">
			{#if positionMargin}
				<!-- fallback to the actual value from the contract - incase something is not right -->
				{addDollarSign(getNumBackInUnits(positionMargin, 18, 2))}
			{:else}
				{position.margin ? '$' + getNumBackInUnits(position.margin, 18, 2) : '-'}
			{/if}
		</div>
		<img
			loading="lazy"
			src={'adjust.png'}
			class="w-4 h-4 hover:rotate-180 transition-all ease-in"
			alt="adjust"
		/>
	</button>

	<UserPositionTpslItem {position} {isCreating} {isTpslUpdating} />
	{#if showClosePosition}
		{#if isUpdating || isCreating || isTpslUpdating}
			<ProcessingText title="Updating" />
		{:else if $closingPosition == false}
			<div class="flex justify-center gap-2">
				<button
					class="text-fx-pink text-sm text-center cursor-pointer underline underline-offset-2"
					on:click|stopPropagation={() => openClosePositionModal('Market')}
				>
					Close
				</button>
			</div>
		{:else}
			<ProcessingText title="Closing" />
		{/if}
	{/if}
</button>

<!-- Mobile view -->
<button class="sm:hidden block mb-2 w-full" on:click={openMarket} on:keydown={openMarket}>
	<GradientBorder
		gradient="bg-fx-gradient-6"
		paddingClass="p-px"
		borderRadius="rounded-xl squircle-xl"
		outerClassList=" w-full h-full"
		innerClassList="flex justify-center items-center bg-fx-black w-full h-full"
	>
		<div class="bg-fx-gradient-8 w-full h-gull rounded-xl squircle-xl p-4 flex-col">
			<div class="flex justify-between mb-1">
				<div class="flex-col items-start mb-2">
					<div class="flex gap-2">
						<img
							loading="lazy"
							src={getTokenLogo(position.marketIdentifier)}
							alt="logo"
							class="w-5 h-5 rounded-full"
						/>
						<div class="text-fx-grey-100 text-sm rounded-lg squircle-lg font-medium">
							{getDisplayName(position.marketIdentifier)}
						</div>

						<GradientBorder
							gradient="bg-fx-gradient-6"
							paddingClass="p-px"
							borderRadius="rounded-lg squircle-lg"
							outerClassList="w-fit h-fit"
							innerClassList="/''bg-fx-black"
						>
							{#if new BigNumber(position.size).gt(0)}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg squircle-lg"
								>
									Long
								</div>
							{:else}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg squircle-lg"
								>
									Short
								</div>
							{/if}
						</GradientBorder>
					</div>
				</div>
				<div class="flex flex-col items-end justify-center">
					<div class="text-fx-grey-100 text-xs font-medium text-center">
						{getNumBackInUnits(position.size, 18, 4)}
						{getDisplayAsset(position.marketIdentifier)}
					</div>
					<div class="text-fx-grey-300 text-xs font-medium text-center">
						{#if position}
							(${num(
								getNumBackInUnits(
									new BigNumber(position.size)
										.multipliedBy($marketPrices[position.marketIdentifier])
										.div(1e18)
										.abs()
										.toString(),
									18,
									2
								)
							)})
						{:else}
							-
						{/if}
					</div>
				</div>
			</div>

			<div class="grid grid-cols-2 mt-3">
				<div class="flex-col">
					<div class="text-xs text-fx-grey-400 text-left">PnL</div>
					<div
						class="text-sm font-medium text-left whitespace-wrap flex"
						class:text-fx-green={isUnrealisedPnlPositive}
						class:text-fx-gradient-22={!isUnrealisedPnlPositive}
					>
						{#if position && !isCreating}
							{#if isUnrealisedPnlPositive}
								{addDollarSign(getNumBackInUnits(positionPnl.toString(), 18, 2))}

								{#if position && position.margin}
									/{new BigNumber(positionPnl)
										.div(new BigNumber(position.margin))
										.multipliedBy(100)
										.toFixed(2)}%
								{/if}
							{:else}
								{addDollarSign(getNumBackInUnits(positionPnl.toString(), 18, 2))}
								{#if position && position.margin}
									/{new BigNumber(positionPnl)
										.div(new BigNumber(position.margin))
										.multipliedBy(100)
										.toFixed(2)}%
								{/if}
							{/if}
							<button on:click|stopPropagation={showShareablePnlPopup}>
								<img
									class="justify-self-center w-4 ml-2"
									loading="lazy"
									src={'share-icon.svg'}
									alt="share"
								/>
							</button>
						{:else}
							-
						{/if}
					</div>
				</div>
				<div class="flex-col">
					<div class="text-xs text-fx-grey-400 text-end">Avg. Entry / Liq. Price</div>
					<div class="text-sm text-fx-grey-200 font-medium text-right">
						{#if isCreating}
							-
						{:else}
							${num(
								getNumBackInUnits(
									position.avgEntryPrice,
									18,
									marketsConfig[position.asset].showDecimals
								)
							)} / ${num(
								getNumBackInUnits(
									position.liquidationPrice,
									18,
									marketsConfig[position.asset].showDecimals
								)
							)}
						{/if}
					</div>
				</div>
			</div>

			<div class="grid grid-cols-2 gap-4 mt-4">
				<div class="flex-col">
					<div class="text-xs text-fx-grey-400 text-left">Entire Position TP/SL</div>
					<UserPositionTpslItem {position} {isTpslUpdating} {isCreating} />
				</div>
				<div class="flex-col">
					<div class="text-xs text-fx-grey-400 text-end">Funding</div>
					<div
						class:text-fx-gradient-23={isAccruedFudningPositive}
						class:text-fx-gradient-22={isAccruedFudningNegative}
						class="text-fx-grey-100 text-sm font-medium text-right"
					>
						{isCreating ? '-' : addDollarSign(getNumBackInUnits(position.accruedFunding, 18, 3))}
					</div>
				</div>
			</div>
			<div class="grid grid-cols-2 gap-4 mt-4">
				<button
					class="border-fx-grey-500 border rounded-md text-center px-2 py-1 text-xs"
					on:click|stopPropagation={() => openAdjustMarginModal(position.marketIdentifier)}
				>
					Adjust Margin
				</button>

				{#if isUpdating || isCreating}
					<ProcessingText title="Updating" />
				{:else}
					<button
						class="border-fx-grey-500 border rounded-md text-center px-2 py-1 text-xs text-fx-pink"
						on:click|stopPropagation={() => openClosePositionModal('Market')}
					>
						Close Position
					</button>
				{/if}
			</div>
		</div>
	</GradientBorder>
</button>

<img loading="lazy" src={'shareable/rocket.png'} alt="sharable" class="hidden" />
